import Modifier from 'ember-modifier';
import { registerDestructor } from '@ember/destroyable';
import { DateTime } from 'scaleflow/utils/date-time';

// Import the echarts core module, which provides the necessary interfaces for using echarts.
import * as echarts from 'echarts/core';
import '../../config/echarts_theme';

// Import bar charts, all suffixed with Chart
import { BarChart } from 'echarts/charts';
import { LineChart } from 'echarts/charts';

// Import the tooltip, title, rectangular coordinate system, dataset and transform components
import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  DatasetComponent,
  TransformComponent,
  LegendComponent,
} from 'echarts/components';

// Features like Universal Transition and Label Layout
import { LabelLayout, UniversalTransition } from 'echarts/features';

// Import the Canvas renderer
// Note that including the CanvasRenderer or SVGRenderer is a required step
import { CanvasRenderer } from 'echarts/renderers';
import { service } from '@ember/service';

// Register the required components
echarts.use([
  BarChart,
  LineChart,
  TitleComponent,
  TooltipComponent,
  GridComponent,
  DatasetComponent,
  TransformComponent,
  LabelLayout,
  UniversalTransition,
  CanvasRenderer,
  LegendComponent,
]);

import COLORS from '../../utils/colors';

function cleanup(instance) {
  instance?.chart?.dispose?.();
}

export default class CapacityRolesModifier extends Modifier {
  @service intl;
  @service store;

  plannedInitiativesLabel = 'Plannable time';

  constructor(owner, args) {
    super(owner, args);
    registerDestructor(this, cleanup);
  }

  async modify(element, [roadmap, initiatives, scenario], options) {
    if (
      this.initiatives == initiatives &&
      this.roadmap == roadmap &&
      this.scenario == scenario
    )
      return;
    this.initiatives = await initiatives;
    this.roadmap = await roadmap;
    this.scenario = await scenario;
    this.options = options;

    const promises = [];
    this.initiatives?.forEach((initiative) => {
      promises.push(this.store.findRecord('bon', initiative.id));
    });
    await Promise.allSettled(promises);
    this.setupChart(element).then(() => this.updateChart());
  }

  async setupChart(element) {
    if (this.chart != undefined) return;

    this.chart = echarts.init(element, 'producthub', {
      height: this.options.height ?? 512,
    });
  }

  updateChart() {
    const options = {
      ...this.chartOptions,
      series: this.series,
      dataset: this.dataset,
    };

    this.chart.setOption(options);
  }

  calculate_default_height() {
    return 150 + this.options.roles.length * 50;
  }

  calculate_default_width() {
    return 150 + this.options.roles.length * 50;
  }

  get chartOptions() {
    let context = this;
    const options = {
      tooltip: {
        trigger: 'axis',
        position: function (point, params, dom, rect, size) {
          // Position tooltip to the right of cursor
          return [point[0] + 20, point[1] - size.contentSize[1] / 2];
        },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#ccc',
        borderWidth: 1,
        padding: 15,
        textStyle: {
          fontFamily: 'Arial',
          fontSize: 12,
          color: '#333',
        },
        extraCssText:
          'box-shadow: 0 3px 10px rgba(0, 0, 0, 0.15); border-radius: 8px;',
        axisPointer: {
          type: 'shadow',
          shadowStyle: {
            color: 'rgba(0, 0, 0, 0.05)',
          },
        },
        formatter: function (params) {
          const data = params[0].data;
          const roleName = params[0].axisValueLabel;
          const percentage = data.percentage;
          const workDetails = data.work_details;
          const capacityDetails = data.capacity_details;

          let output = `<h3>Role: <span class="font-semibold">${roleName}</span></h3>`;

          // Status based on thresholds
          let statusColor, statusText;
          if (!capacityDetails) {
            statusColor = 'gray';
            statusText = 'Unknown';
          } else {
            const totalWork = data.total_work;
            const initiativeCapacity = parseFloat(
              capacityDetails.initiative_capacity,
            );
            const totalCapacity = data.total_capacity;

            if (totalWork > totalCapacity) {
              statusColor = '#FF4136';
              statusText = 'Exceeding Total Capacity';
            } else if (totalWork > initiativeCapacity) {
              statusColor = '#FF851B';
              statusText = 'Using Flexible Capacity';
            } else {
              statusColor = '#2ECC40';
              statusText = 'Within Capacity';
            }
          }

          output += `<h3 class="font-semibold">Workload: <span class="text-right font-bold">${percentage}%</span></h3>`;
          output += `<h3 class="font-semibold">Status: <span style="color: ${statusColor}; font-weight: bold;">${statusText}</span></h3>`;

          // Add breakdown details
          if (workDetails && workDetails.initiatives) {
            // Sort initiatives by workload descending
            const sortedInitiatives = workDetails.initiatives.sort(
              (a, b) => b.workload - a.workload,
            );

            output += '<h3 class="font-semibold mt-2">Initiatives</h3>';
            output += '<table class="w-full">';

            sortedInitiatives.forEach((initiative) => {
              output += context._addWorkloadRow(
                initiative.name,
                initiative.workload,
              );
            });

            output += `<tr style="border-top: 1px solid #ddd;">
              <td class="pr-2">Planned Work</td>
              <td class="text-right font-bold tabular-nums">${Math.round(data.total_work, 1)} pw</td>
              <td></td>
            </tr>`;
            //output += context._addWorkloadRow('<span class="font-semibold">Planned Work</span>', data.total_work);
            output += '</table>';
          }

          // Add capacity information with thresholds
          if (capacityDetails) {
            output += '<h3 class="font-semibold mt-2">Capacity Thresholds</h3>';
            output += '<table class="w-full">';

            const initiativeCapacity = parseFloat(
              capacityDetails.initiative_capacity,
            );
            const flowCapacity = parseFloat(capacityDetails.flow_capacity);
            const totalCapacity = data.total_capacity;
            const totalWork = data.total_work;

            // Initiative capacity with total work percentage
            let totalWorkPercentage =
              initiativeCapacity > 0
                ? Math.round((totalWork / initiativeCapacity) * 100)
                : 0;

            output += `<tr>
              <td class="pr-2">${context.plannedInitiativesLabel}</td>
              <td class="text-right font-bold tabular-nums">${Math.round(initiativeCapacity * 100) / 100} pw</td>
              <td class="text-right font-bold" style="color: ${totalWorkPercentage > 100 ? '#FF4136' : '#2ECC40'}">
                (${totalWorkPercentage}%)
              </td>
            </tr>`;

            // Flow capacity without percentage
            output += `<tr>
              <td class="pr-2">Flexible</td>
              <td class="text-right font-bold tabular-nums">${Math.round(flowCapacity * 100) / 100} pw</td>
              <td></td>
            </tr>`;

            // Overflow indicator if exceeding total capacity
            let overflow = Math.max(0, totalWork - totalCapacity);
            if (overflow > 0) {
              output += `<tr>
                <td class="pr-2">Exceeding Capacity</td>
                <td class="text-right font-bold tabular-nums" style="color: #FF4136">${Math.round(overflow * 100) / 100} pw</td>
                <td class="text-right font-bold" style="color: #FF4136">
                  (${Math.round((overflow / totalCapacity) * 100)}% over)
                </td>
              </tr>`;
            }

            output += `<tr style="border-top: 1px solid #ddd;">
              <td class="pr-2">Total Capacity</td>
              <td class="text-right font-bold tabular-nums">${Math.round(totalCapacity * 100) / 100} pw</td>
              <td></td>
            </tr>`;

            output += '</table>';
          }

          return output;
        },
      },
      backgroundColor: 'rgba(255, 255, 255, 0.6)',
      grid: {
        left: '2%',
        right: '10%',
        bottom: '8%',
        top: '10%',
        containLabel: true,
      },
      legend: {
        textStyle: {
          fontFamily: 'Arial',
          fontSize: 12,
        },
      },
      yAxis: {
        type: 'value',
        name: 'Workload %',
        nameLocation: 'center',
        nameGap: 35,
        nameTextStyle: {
          fontFamily: 'Arial',
          fontSize: 13,
          fontWeight: 'bold',
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: '#ccc',
          },
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: 'rgba(220, 220, 220, 0.5)',
            width: 1,
            type: 'dashed',
          },
        },
        axisLabel: {
          fontFamily: 'Arial',
          fontSize: 11,
          formatter: '{value}%',
        },
        // Markers for capacity thresholds
        markLine: {
          silent: true,
          symbol: ['none', 'none'],
          animation: true,
          animationDuration: 800,
          data: [
            {
              yAxis: 100,
              lineStyle: {
                color: 'rgba(255, 99, 71, 0.6)',
                width: 2,
                type: 'solid',
                shadowBlur: 4,
                shadowColor: 'rgba(255, 99, 71, 0.3)',
              },
              label: {
                formatter: '100% (Full Capacity)',
                position: 'end',
                fontSize: 12,
                fontFamily: 'Arial',
                padding: [4, 8],
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                borderRadius: 3,
                shadowColor: 'rgba(0, 0, 0, 0.2)',
                shadowBlur: 2,
              },
            },
          ],
        },
      },
      xAxis: {
        type: 'category',
        axisLabel: {
          rotate: 45,
          fontFamily: 'Arial',
          fontSize: 11,
          color: '#555',
          margin: 14,
        },
        axisLine: {
          lineStyle: {
            color: '#ccc',
          },
        },
      },
      animation: true,
      animationDuration: 800,
      animationEasing: 'elasticOut',
    };

    if (this.options.formatter) {
      options.tooltip.formatter = this.options.formatter;
    }

    return options;
  }

  // Helper method for tooltip rows
  _addWorkloadRow(label, value) {
    return `<tr>
      <td class="pr-2">${label}</td>
      <td class="text-right font-bold tabular-nums">${Math.round(value * 100) / 100} pw</td>
    </tr>`;
  }

  get series() {
    const barSeries = {
      type: 'bar',
      id: 'workload',
      encode: {
        x: 'role',
        y: 'percentage',
      },
      barWidth: '50%', // Make bars more substantial
      barCategoryGap: '20%', // More space between bars for cleaner look
      itemStyle: {
        color: (params) => {
          const data = params.data;
          const totalWork = data.total_work;
          const capacityDetails = data.capacity_details;

          if (!capacityDetails) return COLORS.vision;

          const initiativeCapacity = parseFloat(
            capacityDetails.initiative_capacity,
          );
          const totalCapacity = data.total_capacity;

          // Nicer gradient colors based on capacity thresholds
          if (totalWork > totalCapacity) {
            return {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: '#FF6259' }, // lighter red
                { offset: 1, color: '#E02A1F' }, // darker red
              ],
            };
          } else if (totalWork > initiativeCapacity) {
            return {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: '#FFA53F' }, // lighter orange
                { offset: 1, color: '#FF7D00' }, // darker orange
              ],
            };
          } else {
            return {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: '#5BEF6A' }, // lighter green
                { offset: 1, color: '#28B637' }, // darker green
              ],
            };
          }
        },
        borderRadius: [5, 5, 0, 0], // Rounded corners at the top
        shadowColor: 'rgba(0, 0, 0, 0.2)',
        shadowBlur: 5,
      },
      label: {
        show: true,
        position: 'top',
        distance: 10,
        formatter: function (params) {
          const data = params.data;
          const totalWork = data.total_work;
          const capacityDetails = data.capacity_details;
          const labelText = data.percentage + '%';

          if (!capacityDetails) return labelText;

          const initiativeCapacity = parseFloat(
            capacityDetails.initiative_capacity,
          );
          const totalCapacity = data.total_capacity;

          // Match label style to the bar color logic
          if (totalWork > totalCapacity) {
            return '{overCapacity|' + labelText + '}';
          } else if (totalWork > initiativeCapacity) {
            return '{flowCapacity|' + labelText + '}';
          } else {
            return '{initiativeCapacity|' + labelText + '}';
          }
        },
        rich: {
          overCapacity: {
            color: '#E02A1F',
            fontWeight: 'bold',
            fontFamily: 'Arial',
            fontSize: 14,
            padding: [2, 7],
            borderRadius: 4,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            shadowColor: 'rgba(224, 42, 31, 0.3)',
            shadowBlur: 5,
          },
          flowCapacity: {
            color: '#FF7D00',
            fontWeight: 'bold',
            fontFamily: 'Arial',
            fontSize: 14,
            padding: [2, 7],
            borderRadius: 4,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            shadowColor: 'rgba(255, 125, 0, 0.3)',
            shadowBlur: 5,
          },
          initiativeCapacity: {
            color: '#28B637',
            fontWeight: 'bold',
            fontFamily: 'Arial',
            fontSize: 14,
            padding: [2, 7],
            borderRadius: 4,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            shadowColor: 'rgba(40, 182, 55, 0.3)',
            shadowBlur: 5,
          },
        },
      },
      markPoint: {
        symbol: 'roundRect',
        symbolSize: [50, 4],
        itemStyle: {
          color: 'rgba(255, 255, 255, 0.85)',
          borderColor: 'rgba(0, 0, 0, 0.3)',
          borderWidth: 1,
          shadowColor: 'rgba(0, 0, 0, 0.3)',
          shadowBlur: 3,
        },
        data: this._getInitiativeCapacityMarkers(),
        silent: true, // Don't trigger events on markers
        animation: true,
        animationDuration: 600,
      },
    };

    return [barSeries];
  }

  // Helper to create initiative capacity markers for each role
  _getInitiativeCapacityMarkers() {
    const markers = [];
    const data = this.dataset[0].source;

    data.forEach((item) => {
      if (!item.capacity_details) return;

      const initiativeCapacity = parseFloat(
        item.capacity_details.initiative_capacity,
      );
      const totalCapacity = item.total_capacity;

      if (totalCapacity > 0) {
        const initiativePercentage = Math.min(
          100,
          (initiativeCapacity / totalCapacity) * 100,
        );

        // Only add threshold if it's visible (above 0% and below 100%)
        if (initiativePercentage > 0 && initiativePercentage < 100) {
          markers.push({
            name: 'Initiative Capacity',
            coord: [item.role, initiativePercentage],
            value: initiativePercentage,
            itemStyle: {
              color: '#000',
            },
            label: {
              show: false,
            },
          });
        }
      }
    });

    return markers;
  }

  get source_buckets_and_uncertainty() {
    let roles_cache;

    if (roles_cache == undefined) {
      roles_cache = {};
      for (let index = 0; index < this.initiatives.length; index++) {
        const initiative = this.initiatives[index];

        const bon = initiative.bon;
        const investments = bon?.get('investments');
        const calculations = investments?.calculations ?? [];

        // Calculate time overlap factor for this initiative
        const overlapFactor = this.calculateTimeOverlap(
          initiative,
          this.roadmap,
        );

        calculations.forEach((calculation) => {
          let role;
          switch (calculation.type) {
            case 'person-weeks':
            case 'investment':
              role = 'unspecified';
              break;
            default:
              role = calculation.role_id;
              break;
          }

          if (role == undefined) role = 'unspecified';

          if (roles_cache[role] == undefined) {
            roles_cache[role] = {
              VISION: 0,
              'CUSTOMER REQUEST CURRENT': 0,
              'OPERATIONS AND MAINTENANCE': 0,
              UNSPECIFIED: 0,
              UNCERTAINTY: 0,
              initiatives: [],
            };
          }

          // Handle undefined or null source
          const source = initiative.source || 'UNSPECIFIED';
          const calculatedWorkload =
            (calculation.people ?? 1) *
            (calculation.weeks ?? 1) *
            (calculation.person_weeks ?? 1) *
            overlapFactor;

          roles_cache[role][source] += calculatedWorkload;
          roles_cache[role]['UNCERTAINTY'] +=
            (calculation.people ?? 1) *
            (calculation.weeks ?? 1) *
            (calculation.person_weeks ?? 1) *
            (calculation.confidence_value - 1) *
            overlapFactor;

          // Track individual initiative for this role
          const existingInitiative = roles_cache[role].initiatives.find(
            (init) => init.id === initiative.id,
          );

          if (existingInitiative) {
            existingInitiative.workload += calculatedWorkload;
          } else {
            roles_cache[role].initiatives.push({
              id: initiative.id,
              name: initiative.title,
              workload: calculatedWorkload,
              source: source,
            });
          }
        });
      }
    }

    const source = [];

    for (let [role_id, values] of Object.entries(roles_cache)) {
      const role = this.roles.find((e) => e.id == role_id);
      const name = role?.name ?? 'Unspecified';

      const source_object = {
        role: name,
        vision: values['VISION'],
        customer: values['CUSTOMER REQUEST CURRENT'],
        operations: values['OPERATIONS AND MAINTENANCE'],
        unspecified: values['UNSPECIFIED'],
        uncertainty: values['UNCERTAINTY'],
        initiatives: values['initiatives'] || [],
      };

      if (name == 'Unspecified') {
        source.unshift(source_object);
      } else {
        source.push(source_object);
      }
    }

    return source;
  }

  get source_initiatives_vs_flow() {
    const role_capacity = [];

    this.roles.forEach((role) => {
      const number_of_people = role.ftes ?? role.people.length;
      role_capacity.push({
        role: role.name,
        initiative_capacity: (
          number_of_people *
          this.percentage_initiatives *
          this.weeks
        )?.toFixed?.(1),
        flow_capacity: (
          number_of_people *
          this.percentage_flow *
          this.weeks
        )?.toFixed?.(1),
      });
    });

    return role_capacity;
  }

  get dataset() {
    // Calculate workload percentages per role
    const percentageSource = this.roles
      .map((role) => {
        const roleName = role.name;
        const roleWork = this.source_buckets_and_uncertainty.find(
          (d) => d.role === roleName,
        );
        const roleCapacity = this.source_initiatives_vs_flow.find(
          (d) => d.role === roleName,
        );

        // Calculate total workload (including unspecified and uncertainty)
        const totalWork = roleWork
          ? roleWork.vision +
            roleWork.customer +
            roleWork.operations +
            roleWork.unspecified +
            roleWork.uncertainty
          : 0;

        // Calculate total capacity
        const totalCapacity = roleCapacity
          ? parseFloat(roleCapacity.initiative_capacity) +
            parseFloat(roleCapacity.flow_capacity)
          : 0;

        // Calculate percentage (handle division by zero)
        const percentage =
          totalCapacity > 0 ? (totalWork / totalCapacity) * 100 : 0;

        return {
          role: roleName,
          percentage: percentage.toFixed(1),
          raw_percentage: percentage, // For coloring logic
          // Keep the original data for tooltips
          total_work: totalWork,
          total_capacity: totalCapacity,
          work_details: roleWork,
          capacity_details: roleCapacity,
        };
      })
      .filter((item) => item.raw_percentage > 0); // Only show roles with work

    return [
      {
        dimensions: [
          { name: 'role', displayName: 'Role' },
          { name: 'percentage', displayName: 'Workload %' },
        ],
        source: percentageSource,
      },
    ];
  }

  /*
   * ACCESSOR METHODS
   */

  get roles() {
    return this.options.roles ?? [];
  }

  get percentage_initiatives() {
    return this.scenario?.allocation.initiatives.percent / 100;
  }

  get percentage_flow() {
    return this.scenario?.allocation.operational_flex.percent / 100;
  }

  get pw_initiatives() {
    return (
      (this.scenario?.get('pw_initiatives') / this.scenario?.get('weeks')) *
      this.roadmap?.get('weeks')
    );
  }

  get pw_flow() {
    return (
      (this.scenario?.get('pw_flow') / this.scenario?.get('weeks')) *
      this.roadmap?.get('weeks')
    );
  }

  get weeks() {
    return this.roadmap?.weeks ?? 0;
  }

  calculateTimeOverlap(initiative, roadmap) {
    // Handle missing data
    if (
      !initiative ||
      !roadmap ||
      !initiative.startDate ||
      !initiative.duration
    ) {
      return 1; // Default to full inclusion if data is missing
    }

    // Get initiative date range
    const initiativeStart = initiative.startDate
      ? new Date(initiative.startDate)
      : undefined;
    const initiativeDurationWeeks = parseInt(initiative.duration, 10);

    if (
      !initiativeStart ||
      initiativeStart === '-' ||
      isNaN(initiativeDurationWeeks)
    ) {
      return 1; // Default to full inclusion if dates can't be parsed
    }

    const initiativeEnd = DateTime.from(initiativeStart)
      .add(initiativeDurationWeeks, 'weeks')
      .toDate();

    // Get roadmap date range
    const roadmapStart = new Date(roadmap.start_date);
    const roadmapEnd = new Date(roadmap.end_date);

    // Calculate overlap
    const overlapStart = new Date(
      Math.max(initiativeStart.getTime(), roadmapStart.getTime()),
    );
    const overlapEnd = new Date(
      Math.min(initiativeEnd.getTime(), roadmapEnd.getTime()),
    );

    // No overlap if start is after end
    if (overlapStart >= overlapEnd) {
      return 0;
    }

    // Calculate overlap duration in milliseconds
    const overlapDuration = overlapEnd.getTime() - overlapStart.getTime();
    const initiativeDuration =
      initiativeEnd.getTime() - initiativeStart.getTime();

    // Return ratio of overlap to total initiative duration
    const overlapRatio = overlapDuration / initiativeDuration;

    // Clamp between 0 and 1
    return Math.max(0, Math.min(1, overlapRatio));
  }
}
